import { Injectable, inject } from '@angular/core';
import { ConfirmModalComponent } from '../../components/modals/confirm-modal/confirm-modal.component';
import { PreviewResultModalComponent } from '../../dynamic-form/modals/preview-result/preview-result.component';
import { Observable, of } from 'rxjs';
import { catchError, map } from 'rxjs/operators';
import { PreviewAnswerProperty, PreviewChart } from '../../models/previewFormResult';
import { TextModalComponent, TextModalData } from '../../dynamic-form/modals/text-modal/text-modal.component';
import {
  ConfirmModalData,
  NewConfirmModalComponent
} from '../../components/modals/new-confirm-modal/new-confirm-modal.component';
import {
  ErrorModalData,
  NewErrorModalComponent
} from '../../components/modals/new-error-modal/new-error-modal.component';
import {
  SaveDraftModalComponent,
  SaveDraftModalData
} from '../../components/modals/save-draft-modal/save-draft-modal.component';
import {
  NewTripleConfirmModalComponent,
  TripleConfirmModalData
} from '../../components/modals/new-triple-confirm-modal/new-triple-confirm-modal.component';
import { DuditDrugModalComponent } from 'Shared/app/static-form/_forms/dudit/1.0/components/dudit-drug-modal.component';
import { DuditModalData } from 'Shared/app/static-form/_forms/dudit/1.0/dudit.component';
import { MatDialog } from '@angular/material/dialog';
import { TextDirection } from 'Shared/app/constants/languages.constants';

@Injectable({
  providedIn: 'root'
})
export class ModalService {
  readonly #matDialog = inject(MatDialog);

  closeAllModals(): void {
    this.#matDialog.closeAll();
  }

  confirm(title?: string, message?: string, confirmButtonText?: string, denyButtonText?: string): Observable<boolean> {
    const dialogRef = this.#matDialog.open(ConfirmModalComponent, {
      data: {
        title: title ?? 'Är du säker',
        message: message ?? 'Bekräfta',
        confirmText: confirmButtonText ?? 'Ja',
        denyText: denyButtonText ?? 'Nej'
      } as ConfirmModalData,
      panelClass: 'modal-dialog',
      position: { top: '20px' },
      width: '90vw',
      maxWidth: '700px',
      autoFocus: 'dialog'
    });

    return dialogRef.afterClosed().pipe(map((result: boolean) => result ?? false));
  }

  info(title: string, message: string, buttonText: string): void {
    this.#matDialog.open(TextModalComponent, {
      data: {
        title: title,
        message: message,
        buttonText: buttonText
      } as TextModalData,
      panelClass: 'modal-dialog',
      position: { top: '20px' },
      width: '90vw',
      maxWidth: '700px',
      autoFocus: 'dialog'
    });
  }

  previewResult(answerProperties: PreviewAnswerProperty[], charts: PreviewChart[] | null): void {
    this.#matDialog.open(PreviewResultModalComponent, {
      data: {
        charts: charts,
        answers: answerProperties
      },
      panelClass: 'modal-dialog',
      position: { top: '20px' },
      minWidth: '700px',
      autoFocus: 'dialog'
    });
  }

  /**
   *
   * Param buttonstyles is a key-value pair with keys defined as "confirm" and "deny". Both or none must be supplied, default values are "primary" and "warning"
   *
   * Param disableClose keeps the dialog open on Escape and backdrop click, which otherwise answer false
   */
  newConfirm(
    title?: string,
    message?: string,
    subMessage?: string,
    confirmButtonText?: string,
    denyButtonText?: string,
    buttonStyles?: object,
    revertButtons?: boolean,
    templateDirection?: TextDirection,
    disableClose?: boolean
  ): Observable<boolean> {
    const dialogRef = this.#matDialog.open(NewConfirmModalComponent, {
      data: {
        title: title ?? 'Är du säker?',
        message: message ?? 'Bekräfta',
        subMessage: subMessage ?? '',
        confirmText: confirmButtonText ?? 'Ja',
        denyText: denyButtonText ?? 'Nej',
        buttonStyles: buttonStyles ?? {
          confirm: 'primary',
          deny: 'warning'
        },
        revertButtons: revertButtons ?? false
      } as ConfirmModalData,
      panelClass: 'wfs-new-confirm-modal',
      autoFocus: 'dialog',
      direction: templateDirection ?? 'ltr',
      disableClose: disableClose ?? false
    });

    return dialogRef.afterClosed().pipe(
      map((result: boolean) => result ?? false),
      catchError(() => {
        return of(false);
      })
    );
  }

  /**
   *
   * Param buttonstyles is a key-value pair with keys defined as "first","second" and "third". All or none must be supplied, default values are "primary", "info" and "warning"
   */
  newTripleConfirm(
    title?: string,
    message?: string,
    subMessage?: string,
    firstButtonText?: string,
    secondButtonText?: string,
    thirdButtonText?: string,
    buttonStyles?: object,
    revertButtons?: boolean
  ): Observable<number> {
    const dialogRef = this.#matDialog.open(NewTripleConfirmModalComponent, {
      data: {
        title: title ?? 'Är du säker?',
        message: message ?? 'Bekräfta',
        subMessage: subMessage ?? '',
        firstText: firstButtonText ?? 'Ja',
        secondText: secondButtonText ?? 'Nej',
        thirdText: thirdButtonText ?? 'Fler alternativ',
        buttonStyles: buttonStyles ?? {
          first: 'info',
          second: 'primary',
          third: 'warning'
        },
        revertButtons: revertButtons ?? false
      } as TripleConfirmModalData,
      panelClass: 'wfs-new-triple-confirm-modal',
      autoFocus: 'dialog'
    });

    return dialogRef.afterClosed().pipe(
      map((result) => result ?? undefined),
      catchError(() => {
        return of(undefined);
      })
    );
  }

  newError(
    title?: string,
    message?: string,
    subMessage?: string,
    confirmButtonText?: string,
    denyButtonText?: string,
    buttonStyles?: object,
    revertButtons?: boolean,
    useDefault?: boolean
  ): Observable<boolean> {
    const dialogRef = this.#matDialog.open(NewErrorModalComponent, {
      data: {
        title: title ?? 'Något gick fel',
        message: message ?? '',
        subMessage: subMessage ?? '',
        confirmText: confirmButtonText ?? '',
        denyText: denyButtonText ?? 'Stäng',
        buttonStyles: buttonStyles ?? {
          confirm: 'warning',
          deny: 'warning'
        },
        revertButtons: revertButtons ?? false,
        useDefault: useDefault ?? true
      } as ErrorModalData,
      panelClass: 'wfs-new-error-modal',
      autoFocus: 'dialog'
    });

    return dialogRef.afterClosed().pipe(
      map((result: boolean) => result ?? false),
      catchError(() => {
        return of(undefined);
      })
    );
  }

  // Modal for confirming draft saving
  saveDraftConfirm(
    title?: string,
    message?: string,
    confirmCloseText?: string,
    confirmContinueText?: string,
    denyText?: string,
    direction?: string
  ): Observable<string> {
    const dialogRef = this.#matDialog.open(SaveDraftModalComponent, {
      data: {
        title: title ? title : 'Spara utkast', // shared.draft.title
        message: message ? message : 'Är du säker på att du vill spara ett utkast?', // shared.draft.text
        confirmCloseText: confirmCloseText ? confirmCloseText : 'Spara och stäng', // shared.save.close
        confirmContinueText: confirmContinueText ? confirmContinueText : 'Spara och fortsätt', // shared.save.continue
        denyText: denyText ? denyText : 'Avbryt',
        direction: direction ? direction : 'ltr'
      } as SaveDraftModalData,
      disableClose: true,
      position: { top: '20px' },
      panelClass: 'wfs-save-draft-modal',
      autoFocus: 'dialog'
    });

    return dialogRef.afterClosed().pipe(
      map((result: string) => result ?? 'cancel'),
      catchError(() => {
        return of('cancel');
      })
    );
  }

  duditDrugListModal(modalData: DuditModalData[]): void {
    this.#matDialog.open(DuditDrugModalComponent, {
      data: modalData,
      panelClass: 'wfs-dudit-drug-modal',
      autoFocus: 'dialog'
    });
  }
}
